import { getSaved, save, unsave } from "@/api/me";
import { getToken, onSessionChange } from "@/auth/session";

/* Which listings the signed-in member has saved.
 *
 * One fetch per session, shared by every card on the page, so a results grid
 * of twenty cards does not make twenty requests. Guests never trigger it --
 * the store stays empty and the heart routes them to sign-in instead.
 *
 * The set is the server's answer, not a mirror kept in localStorage. Legacy
 * kept a parallel copy in `ejarmasr_member_saved` with nothing reconciling it
 * against `saved_properties` (inventory 6.10); this holds the server's ids in
 * memory for the tab and refetches after a sign-in.
 */

let ids: Set<string> = new Set();
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

export function subscribeSaved(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isSaved(listingId: string): boolean {
  return ids.has(listingId);
}

/** Loads once per signed-in session. A guest call is a no-op. */
export function ensureSavedLoaded(): void {
  if (loaded || inflight || !getToken()) return;
  inflight = getSaved()
    .then((rows) => {
      ids = new Set(rows.map((r) => r.listing_id).filter((v): v is string => !!v));
      loaded = true;
      notify();
    })
    .catch(() => { /* a failed read leaves the hearts unfilled, never wrong-filled */ })
    .finally(() => { inflight = null; });
}

/** Optimistic toggle that reverts if the server refuses. */
export async function toggleSaved(listingId: string): Promise<boolean> {
  const was = ids.has(listingId);
  const next = new Set(ids);
  if (was) next.delete(listingId); else next.add(listingId);
  ids = next;
  notify();
  try {
    if (was) await unsave(listingId); else await save(listingId);
    return !was;
  } catch (err) {
    const reverted = new Set(ids);
    if (was) reverted.add(listingId); else reverted.delete(listingId);
    ids = reverted;
    notify();
    throw err;
  }
}

/* A sign-in or sign-out invalidates the set: the next reader refetches, and a
 * signed-out tab must not keep showing the previous member's hearts. */
onSessionChange(() => { ids = new Set(); loaded = false; notify(); });
